// \src\components\base\MathCaptcha.tsx

import { useState, useEffect, useCallback } from "react";

interface MathCaptchaProps {
  onVerify: (verified: boolean) => void;
  serverEndpoint?: string;
  onVerificationToken?: (token: string) => void;
}

interface ServerCaptchaResponse {
  success?: boolean;
  message?: string;
  question?: string;
  token?: string;
  verified?: boolean;
  verificationToken?: string;
}

function generateChallenge() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  const ops = ["+", "-", "x"] as const;
  const op = ops[Math.floor(Math.random() * ops.length)];

  let answer: number;

  if (op === "+") {
    answer = a + b;
  } else if (op === "-") {
    answer = Math.max(a, b) - Math.min(a, b);
  } else {
    answer = a * b;
  }

  const question =
    op === "-"
      ? `${Math.max(a, b)} ${op} ${Math.min(a, b)}`
      : `${a} ${op} ${b}`;

  return {
    question,
    answer,
  };
}

async function parseServerResponse(
  response: Response,
): Promise<ServerCaptchaResponse> {
  const text = await response.text();

  if (!text.trim()) {
    return {};
  }

  try {
    return JSON.parse(text) as ServerCaptchaResponse;
  } catch {
    throw new Error(
      "O servidor retornou uma resposta inválida para o captcha.",
    );
  }
}

export default function MathCaptcha({
  onVerify,
  serverEndpoint,
  onVerificationToken,
}: MathCaptchaProps) {
  const serverMode = Boolean(serverEndpoint);

  const [challenge, setChallenge] = useState(generateChallenge);
  const [serverQuestion, setServerQuestion] = useState("");
  const [serverToken, setServerToken] = useState("");
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<
    "idle" | "correct" | "wrong"
  >("idle");
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const resetVerification = useCallback(() => {
    setInput("");
    setStatus("idle");
    setServerError("");
    onVerify(false);
    onVerificationToken?.("");
  }, [onVerify, onVerificationToken]);

  const loadServerChallenge = useCallback(async () => {
    if (!serverEndpoint) {
      return;
    }

    setLoading(true);
    resetVerification();

    try {
      const separator = serverEndpoint.includes("?") ? "&" : "?";

      const response = await fetch(
        `${serverEndpoint}${separator}action=captcha`,
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        },
      );

      const result = await parseServerResponse(response);

      if (
        !response.ok ||
        result.success !== true ||
        !result.question ||
        !result.token
      ) {
        throw new Error(
          result.message ||
            "Não foi possível gerar a verificação de segurança.",
        );
      }

      setServerQuestion(result.question);
      setServerToken(result.token);
    } catch (error) {
      setServerQuestion("");
      setServerToken("");
      setServerError(
        error instanceof Error
          ? error.message
          : "Não foi possível gerar a verificação de segurança.",
      );
    } finally {
      setLoading(false);
    }
  }, [resetVerification, serverEndpoint]);

  const refresh = useCallback(() => {
    if (serverMode) {
      void loadServerChallenge();
      return;
    }

    setChallenge(generateChallenge());
    resetVerification();
  }, [loadServerChallenge, resetVerification, serverMode]);

  useEffect(() => {
    if (serverMode) {
      void loadServerChallenge();
    }
  }, [loadServerChallenge, serverMode]);

  useEffect(() => {
    if (serverMode) {
      return;
    }

    if (input === "") {
      setStatus("idle");
      onVerify(false);
      return;
    }

    const value = parseInt(input, 10);

    if (!Number.isNaN(value)) {
      if (value === challenge.answer) {
        setStatus("correct");
        onVerify(true);
      } else {
        setStatus("wrong");
        onVerify(false);
      }
    }
  }, [input, challenge.answer, onVerify, serverMode]);

  const handleInputChange = (
    value: string,
  ) => {
    setInput(value);

    if (serverMode) {
      setStatus("idle");
      setServerError("");
      onVerify(false);
      onVerificationToken?.("");
    }
  };

  const verifyServerCaptcha = async () => {
    if (
      !serverEndpoint ||
      !serverToken ||
      input.trim() === ""
    ) {
      setServerError(
        "Digite a resposta da verificação de segurança.",
      );
      return;
    }

    setLoading(true);
    setServerError("");

    try {
      const response = await fetch(serverEndpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          action: "verify_captcha",
          token: serverToken,
          answer: input,
        }),
      });

      const result = await parseServerResponse(response);

      if (
        !response.ok ||
        result.success !== true ||
        result.verified !== true ||
        !result.verificationToken
      ) {
        setStatus("wrong");
        onVerify(false);
        onVerificationToken?.("");

        throw new Error(
          result.message ||
            "Resposta incorreta. Tente novamente.",
        );
      }

      setStatus("correct");
      onVerify(true);
      onVerificationToken?.(
        result.verificationToken,
      );
    } catch (error) {
      setStatus("wrong");
      onVerify(false);
      onVerificationToken?.("");
      setServerError(
        error instanceof Error
          ? error.message
          : "Não foi possível validar a resposta.",
      );
    } finally {
      setLoading(false);
    }
  };

  const displayedQuestion = serverMode
    ? serverQuestion
    : challenge.question;

  return (
    <div className="border border-background-300 rounded-md p-4 bg-background-100">
      <p className="text-xs font-label font-semibold text-foreground-600 uppercase tracking-wide mb-3">
        <i className="ri-shield-check-line mr-1.5 text-primary-500" />
        Verificação de Segurança
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-background-50 border border-background-300 rounded-md px-4 py-2.5 select-none min-h-[46px]">
          {loading && !displayedQuestion ? (
            <i className="ri-loader-4-line animate-spin text-primary-500" />
          ) : (
            <span className="font-heading font-bold text-foreground-950 text-lg tracking-widest">
              {displayedQuestion
                ? `${displayedQuestion} = ?`
                : "Não foi possível gerar o desafio"}
            </span>
          )}
        </div>

        <div className="relative">
          <input
            type="number"
            value={input}
            onChange={(event) =>
              handleInputChange(event.target.value)
            }
            placeholder="Resposta"
            min={0}
            max={99}
            disabled={
              loading ||
              (serverMode && !serverToken) ||
              status === "correct"
            }
            className={`w-24 px-3 py-2.5 text-sm text-center border rounded-md font-label focus:outline-none transition-colors ${
              status === "correct"
                ? "border-green-400 bg-green-50 text-green-700"
                : status === "wrong"
                  ? "border-red-400 bg-red-50 text-red-700"
                  : "border-background-300 bg-background-50 focus:border-primary-400"
            }`}
          />

          {status === "correct" && (
            <i className="ri-checkbox-circle-fill absolute right-2 top-1/2 -translate-y-1/2 text-green-500 text-base" />
          )}

          {status === "wrong" && (
            <i className="ri-close-circle-fill absolute right-2 top-1/2 -translate-y-1/2 text-red-500 text-base" />
          )}
        </div>

        {serverMode && status !== "correct" && (
          <button
            type="button"
            onClick={verifyServerCaptcha}
            disabled={
              loading ||
              !serverToken ||
              input.trim() === ""
            }
            className="h-10 px-4 flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-50 rounded-md text-xs font-label font-semibold transition-colors cursor-pointer"
          >
            {loading ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-check-line" />
            )}
            Verificar
          </button>
        )}

        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          title="Gerar novo desafio"
          className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-primary-500 border border-background-300 rounded-md bg-background-50 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
        >
          <i className="ri-refresh-line text-base" />
        </button>

        {status === "correct" && (
          <span className="text-green-600 text-xs font-label font-semibold flex items-center gap-1">
            <i className="ri-check-line" />
            Verificado!
          </span>
        )}

        {status === "wrong" && input !== "" && (
          <span className="text-red-500 text-xs font-label">
            Resposta incorreta
          </span>
        )}
      </div>

      {serverError && (
        <p className="text-red-600 text-xs font-label mt-3">
          {serverError}
        </p>
      )}
    </div>
  );
}
